import { AxiosService } from '../../axios/axios.service';
import {
  tAutoMessagesResponse,
  tAutoMessagesSortResponse,
  tCreateAutoMessageRequest,
  tAutoMessageResponse,
  tCommentToMessageResponse,
  tToggleResponse,
  tOAuthAuthorizeResponse,
  tTikTokPermissions,
  tTikTokProfile,
} from './TikTok.types';

type tBFFResponse<T> = { data: T };

export const TikTokService = {
  getPermissions: async (workspaceId: string): Promise<tTikTokPermissions> => {
    const response = await AxiosService.get<tBFFResponse<tTikTokPermissions>>(
      `/tiktok-api/accounts/permissions?workspace_id=${encodeURIComponent(workspaceId)}`,
      { isBFFService: true, redactLog: true },
    );
    return response.data;
  },
  getProfile: async (workspaceId: string): Promise<tTikTokProfile> => {
    const response = await AxiosService.get<tBFFResponse<tTikTokProfile>>(
      `/tiktok-api/accounts/profile?workspace_id=${encodeURIComponent(workspaceId)}`,
      { isBFFService: true, redactLog: true },
    );
    return response.data;
  },
  // Only 204 disconnects (the contract), so the call admits no other status: any other answer, a 2xx
  // included, rejects and keeps the account. A 409 or 424 carries what stayed on (TikTok.account.ts).
  disconnect: async (opts: {
    workspaceId: string;
    authorizationVersion: string;
    disableAutomaticMessages: boolean;
  }): Promise<void> => {
    await AxiosService.post(
      '/tiktok-api/accounts/disconnect',
      {
        workspace_id: opts.workspaceId,
        authorization_version: opts.authorizationVersion,
        disable_automatic_messages: opts.disableAutomaticMessages,
      },
      { isBFFService: true, redactLog: true, validateStatus: (status: number) => status === 204 },
    );
  },
  senderAction: async (
    workspaceId: string,
    leadId: string,
    action: 'TYPING' | 'MARK_READ',
  ): Promise<void> => {
    await AxiosService.post(
      `/workspace/${workspaceId}/lead/${leadId}/tiktok/sender-action`,
      { action },
      { isBFFService: true },
    );
  },
  getOAuthUrl: async function (
    workspaceId: string,
  ): Promise<tOAuthAuthorizeResponse> {
    const response = await AxiosService.get<
      tBFFResponse<tOAuthAuthorizeResponse>
    >(`/tiktok-api/auth/authorize?workspace_id=${workspaceId}`, {
      isBFFService: true,
      withCredentials: true,
      redactLog: true,
    });
    return response.data;
  },

  getAutoMessages: async function (
    workspaceId: string,
    type: string,
  ): Promise<tAutoMessagesResponse> {
    const response = await AxiosService.get<
      tBFFResponse<tAutoMessagesResponse>
    >(`/tiktok-api/auto-messages?workspace_id=${workspaceId}&type=${type}`, {
      isBFFService: true,
    });
    return response.data;
  },

  createAutoMessage: async function (
    workspaceId: string,
    request: tCreateAutoMessageRequest,
  ): Promise<tAutoMessageResponse> {
    const response = await AxiosService.post<
      tCreateAutoMessageRequest & { workspace_id: string },
      tBFFResponse<tAutoMessageResponse>
    >(
      `/tiktok-api/auto-messages`,
      { ...request, workspace_id: workspaceId },
      { isBFFService: true },
    );
    return response.data;
  },

  updateAutoMessage: async function (opts: {
    workspaceId: string;
    tiktokAutoMessageId: string;
    request: tCreateAutoMessageRequest;
  }): Promise<tAutoMessageResponse> {
    const response = await AxiosService.put<
      { workspace_id: string; content: tCreateAutoMessageRequest['content'] },
      tBFFResponse<tAutoMessageResponse>
    >(
      `/tiktok-api/auto-messages/${opts.tiktokAutoMessageId}`,
      { workspace_id: opts.workspaceId, content: opts.request.content },
      { isBFFService: true },
    );
    return response.data;
  },

  deleteAutoMessage: async function (
    workspaceId: string,
    tiktokAutoMessageId: string,
  ): Promise<void> {
    await AxiosService.delete(
      `/tiktok-api/auto-messages/${tiktokAutoMessageId}`,
      { isBFFService: true, data: { workspace_id: workspaceId } },
    );
  },

  toggleAutoMessages: async function (opts: {
    workspaceId: string;
    autoMessageType: string;
    enabled: boolean;
  }): Promise<tToggleResponse> {
    const response = await AxiosService.post<
      object,
      tBFFResponse<tToggleResponse>
    >(
      `/tiktok-api/auto-messages/toggle`,
      {
        workspace_id: opts.workspaceId,
        auto_message_type: opts.autoMessageType,
        enabled: opts.enabled,
      },
      { isBFFService: true },
    );
    return response.data;
  },

  sortAutoMessages: async function (opts: {
    workspaceId: string;
    autoMessageType: string;
    tiktokAutoMessageIds: string[];
  }): Promise<tAutoMessagesSortResponse> {
    const response = await AxiosService.post<
      object,
      tBFFResponse<tAutoMessagesSortResponse>
    >(
      `/tiktok-api/auto-messages/sort`,
      {
        workspace_id: opts.workspaceId,
        auto_message_type: opts.autoMessageType,
        tiktok_auto_message_ids: opts.tiktokAutoMessageIds,
      },
      { isBFFService: true },
    );
    return response.data;
  },

  getCommentToMessage: async function (
    workspaceId: string,
  ): Promise<tCommentToMessageResponse> {
    const response = await AxiosService.get<
      tBFFResponse<tCommentToMessageResponse>
    >(`/tiktok-api/comment-to-message?workspace_id=${workspaceId}`, {
      isBFFService: true,
    });
    return response.data;
  },

  toggleCommentToMessage: async function (
    workspaceId: string,
    enabled: boolean,
  ): Promise<tToggleResponse> {
    const response = await AxiosService.post<
      object,
      tBFFResponse<tToggleResponse>
    >(
      `/tiktok-api/comment-to-message/toggle`,
      { workspace_id: workspaceId, enabled },
      { isBFFService: true },
    );
    return response.data;
  },
};
